import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { AlgoliaSearchParams } from "@omni/types/api/search/algolia/search/algoliaSearchParams.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";

import { ProductSliderSkeletonComponent } from "@/components/Product/ProductSlider/components/skeleton/productSliderSkeleton.component";
import { ProductSliderSourceContextProvider } from "@/components/Product/ProductSlider/context/productSliderSource.context";
import { filterVisible } from "@/components/Product/ProductSlider/productSlider.helpers";
import * as AlgoliaSearchService from "@/services/algolia/algoliaSearch.service";
import { useSearchClient } from "@/utils/hooks/algolia/useSearchClient.hook";

type AlgoliaSearchProductSliderProps = {
    algoliaSearchParams: AlgoliaSearchParams;
    serverSideProducts?: Array<Product<ProductFieldSet.ProductTile>>;
};

export const AlgoliaSearchProductSlider = ({
    algoliaSearchParams,
    serverSideProducts,
    children
}: RequireChildrenFor<AlgoliaSearchProductSliderProps>) => {
    const searchClient = useSearchClient();

    const [products, setProducts] = useState(serverSideProducts);
    const [loading, setLoading] = useState<boolean | undefined>(undefined);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (isDefined(products)) return;
        if (loading || error) return;
        if (!isDefined(algoliaSearchParams) || !isDefined(searchClient)) return;

        setLoading(true);

        AlgoliaSearchService.searchProducts(searchClient)(algoliaSearchParams)
            .then(filterVisible)
            .then(setProducts)
            .catch((error: any) => {
                console.warn(
                    "Algolia ProductLister: unable to lookup products",
                    error
                );

                setError(true);
            })
            .finally(() => setLoading(false));
    }, [products, loading, algoliaSearchParams, searchClient, error]);

    if (error) {
        return null;
    } else if (isDefined(products) && isEmpty(products)) {
        return null;
    } else if (isDefined(products) && !isEmpty(products)) {
        return (
            <ProductSliderSourceContextProvider products={products}>
                {children}
            </ProductSliderSourceContextProvider>
        );
    } else {
        return <ProductSliderSkeletonComponent />;
    }
};
